import React from "react";
import { Image, StyleSheet } from "react-native";
import {
  Page,
  Button,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { GenerateAiIconRootParamList } from "../../../../types";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Colors, View } from "react-native-ui-lib";
import { Ionicons } from "@expo/vector-icons";
import { StyleItem, StylesGrid } from "../../../../components/StylesGrid";
import { MUTATION_EXECUTE_QUICK_WIN } from "../../../../utilities/llm-quick-wins";
import {
  ExecuteLlmQuickWinResult,
  LlmImageStyle,
} from "@towersystems/roam-common/lib/generated-types";
import { useMutation } from "@apollo/client";
import { GenerateAiIconFormValues } from "../../../../components/GenerateAiIconForm";
import { useFormContext } from "react-hook-form";
import { GenerateIconScreenContext } from "./common";

export type PreviewIconScreenProps = NativeStackScreenProps<
  GenerateAiIconRootParamList,
  "PreviewIconScreen"
>;

export const PreviewIconScreen = ({
  navigation,
  route,
}: PreviewIconScreenProps) => {
  const [generating, setGenerating] = React.useState(false);
  const flashMessagesContext = useFlashMessages();

  const maybeScreenContext = React.useContext(GenerateIconScreenContext);

  const { getValues, setValue, watch } =
    useFormContext<GenerateAiIconFormValues>();

  const imageUris = watch("generatedBase64Images") ?? [];

  const [m] = useMutation<{
    executeLlmQuickWin: ExecuteLlmQuickWinResult;
  }>(MUTATION_EXECUTE_QUICK_WIN);

  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(
    imageUris.length ? imageUris.length - 1 : null
  );

  const handleOnSubmit = async () => {
    if (selectedIndex !== null) {
      const selectedImage = imageUris[selectedIndex];
      await maybeScreenContext.onSubmit(selectedImage);
    }
    navigation.getParent()?.goBack();
  };

  const handleOnGenerateIcon = async () => {
    const { description, style, colorScheme } = getValues();
    setGenerating(true);
    return m({
      variables: {
        input: {
          generate_icon_quick_win: {
            description: description,
            style: style[0] as LlmImageStyle,
            colorScheme: colorScheme,
          },
        },
      },
    })
      .then((response) => {
        const result = response.data?.executeLlmQuickWin;
        if (result && "result" in result) {
          const imageUrl = result.result;
          const amount = imageUris.length;
          const updatedImages = [...imageUris, imageUrl];
          setValue("generatedBase64Images", updatedImages, {
            shouldDirty: true,
            shouldValidate: true,
          });
          setSelectedIndex(amount);
        } else {
          throw new Error("No result from icon generation");
        }
      })
      .catch((error) => {
        flashMessagesContext.showMessage({
          message: "Failed to generate icon. Please try again.",
          severity: "error",
        });
      })
      .finally(() => {
        setGenerating(false);
      });
  };

  const footerMarkup = (
    <View row>
      <View marginR-10 flex>
        <Button
          disabled={generating}
          label={generating ? "Generating..." : "Generate New Icon"}
          isAction
          onPress={handleOnGenerateIcon}
          icon={<Ionicons name="sparkles" size={17} color={Colors.action} />}
        ></Button>
      </View>
      <View flex>
        <Button
          disabled={generating || selectedIndex === null}
          label={"Confirm Icon"}
          backgroundColor={Colors.action}
          color="white"
          onPress={() => handleOnSubmit()}
        ></Button>
      </View>
    </View>
  );

  const imagePreviewItems: StyleItem[] = imageUris.map((uri, index) => ({
    key: index.toString(),
    base64: uri,
  }));

  const stylesGrid = (
    <StylesGrid
      styles={imagePreviewItems}
      selectedStyle={selectedIndex?.toString()}
      onSelect={(v) => setSelectedIndex(parseInt(v))}
      maxColumns={15}
    />
  );

  const imageUrl = imagePreviewItems[selectedIndex || 0]?.base64
    ? `data:image/png;base64,${imagePreviewItems[selectedIndex || 0]?.base64}`
    : undefined;

  return (
    <Page
      title="Preview Image"
      secondaryTitle="Review and confirm your icon, or generate a new one"
      footer={footerMarkup}
    >
      <View>{stylesGrid}</View>
      <View
        style={{
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          marginTop: 20,
        }}
      >
        <Image
          source={{
            uri: imageUrl,
          }}
          resizeMode="contain"
          style={{
            ...styles.image,
            opacity: generating ? 0.5 : 1,
          }}
        />
      </View>
    </Page>
  );
};

const styles = StyleSheet.create({
  image: {
    width: 400,
    borderRadius: 16,
    backgroundColor: "#f1f1f1",
    height: 400,
  },
});
